import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CouponPreview, CreditPackage, UserSettings } from '../../types';
import { PAYMENT_CONFIG, UI_CONFIG, CONTACT_CONFIG, APP_MODE, PAYOS_POPUP_WINDOW_NAME } from '../../config';

const CREDIT = UI_CONFIG.CREDIT_LABEL || 'Tín dụng';
import { XMarkIcon, CheckIcon, ClockIcon, CopyIcon, SparklesIcon, CreditCardIcon, BoltIcon, InformationCircleIcon, ExclamationTriangleIcon, WalletIcon, CloudArrowUpIcon, PhotoIcon } from './Icons';
import Spinner from './Spinner';
import { confirmManualPayment, previewCreditCoupon } from '../../services/apiService';
import { payosDebugError, payosDebugLog, payosDebugWarn } from '../../utils/payosDebug';

export type PaymentOrderData = {
    orderId: number;
    transactionCode: string;
    checkoutUrl?: string;
    amountVnd?: number;
    baseCredits?: number;
    bonusCredits?: number;
    totalCredits?: number;
    coupon?: CouponPreview['coupon'] | null;
};

type PaymentMethodTab = 'payos' | 'bank' | 'momo';

interface PaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onManualProofSubmitted?: (data: { orderId: number; proofImage: string | null; proofNote: string }) => void;
    pkg: CreditPackage;
    user: UserSettings;
    onCreateOrder: (pkg: CreditPackage, provider: 'payos' | 'bank' | 'momo', couponCode?: string) => Promise<PaymentOrderData | null>;
    onOrderCreated?: (data: { packageId: string; transactionCode: string; orderId: number; coupon?: CouponPreview['coupon'] | null; baseCredits?: number; bonusCredits?: number; totalCredits?: number; amountVnd?: number }) => void;
    initialProvider?: PaymentMethodTab;
    initialProofImageUrl?: string | null;
    initialProofNote?: string | null;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose, onConfirm, onManualProofSubmitted, pkg, user, onCreateOrder, onOrderCreated, initialProvider, initialProofImageUrl, initialProofNote }) => {
    const [activeTab, setActiveTab] = useState<PaymentMethodTab>('payos');
    const [isConfirming, setIsConfirming] = useState(false);
    const [confirmationStatus, setConfirmationStatus] = useState<'verifying' | 'success' | 'pending' | 'idle'>('idle');
    const [copied, setCopied] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [payosFlowLocked, setPayosFlowLocked] = useState(false);
    const payosPopupRef = useRef<Window | null>(null);
    const [orderData, setOrderData] = useState<PaymentOrderData | null>(null);
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);
    const confirmCalled = useRef(false); // Flag để đảm bảo onConfirm chỉ gọi 1 lần
    const [couponCode, setCouponCode] = useState('');
    const [couponPreview, setCouponPreview] = useState<CouponPreview | null>(null);
    const [couponError, setCouponError] = useState<string | null>(null);
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

    // Proof of payment state
    const [proofFile, setProofFile] = useState<File | null>(null);
    const [proofPreview, setProofPreview] = useState<string | null>(null);
    const [proofNote, setProofNote] = useState('');
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const isProofNoteRequired = PAYMENT_CONFIG.manualProofNoteRequired;

    const handleFile = (file: File) => {
        if (!file.type.startsWith('image/')) {
            setError('Vui lòng chỉ tải lên tệp hình ảnh.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setError('Dung lượng ảnh không được vượt quá 5MB.');
            return;
        }
        setProofFile(file);
        setProofPreview(URL.createObjectURL(file));
        setError(null);
    };

    const onDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const showBank = PAYMENT_CONFIG.methods.bankTransfer?.enabled;
    const showMomo = PAYMENT_CONFIG.methods.momo?.enabled;
    const showPayOS = PAYMENT_CONFIG.methods.payos?.enabled;

    // Lấy config động mỗi khi modal được mở để đảm bảo phản ánh đúng cấu hình mới nhất từ backend
    const [methods, setMethods] = useState({
        bank: !!showBank,
        momo: !!showMomo,
        payos: !!showPayOS
    });

    useEffect(() => {
        if (isOpen) {
            const currentBank = PAYMENT_CONFIG.methods.bankTransfer?.enabled;
            const currentMomo = PAYMENT_CONFIG.methods.momo?.enabled;
            const currentPayOS = PAYMENT_CONFIG.methods.payos?.enabled;
            
            setMethods({
                bank: !!currentBank,
                momo: !!currentMomo,
                payos: !!currentPayOS
            });

            const availableTabs: PaymentMethodTab[] = [];
            if (currentPayOS) availableTabs.push('payos');
            if (currentBank) availableTabs.push('bank');
            if (currentMomo) availableTabs.push('momo');

            const preferredTab = initialProvider && availableTabs.includes(initialProvider)
                ? initialProvider
                : (currentPayOS ? 'payos' : (currentBank ? 'bank' : 'momo'));

            setActiveTab(preferredTab);
            setProofFile(null);
            setProofPreview(initialProofImageUrl ?? null);
            setProofNote(initialProofNote ?? '');
            setError(null);
            setCouponCode(user.paymentDetails?.coupon?.code ?? '');
            setCouponPreview(null);
            setCouponError(null);
        }
    }, [isOpen, initialProvider, initialProofImageUrl, initialProofNote, user.paymentDetails?.coupon?.code]);

    const showBankMethod = methods.bank;
    const showMomoMethod = methods.momo;
    const showPayOSMethod = methods.payos;
    const hasAutomation = showPayOSMethod;

    useEffect(() => {
        let autoCloseTimer: any;
        const handleSuccess = (e: any) => {
            payosDebugLog('modal_received_success_event', {
                hasOrderData: !!orderData,
                detail: e?.detail ?? null,
            });
            setConfirmationStatus('success');
            setPayosFlowLocked(false);
            payosPopupRef.current = null;
            // onConfirm() đã được gọi bởi App.tsx master listener
            
            // Tự động đóng modal sau 5 giây nếu người dùng không nhấn nút
            autoCloseTimer = setTimeout(() => {
                resetState();
            }, 5000);
        };
        const handleError = (e: any) => {
            payosDebugWarn('modal_received_error_event', {
                hasOrderData: !!orderData,
                detail: e?.detail ?? null,
            });
            setPayosFlowLocked(false);
            payosPopupRef.current = null;
            if (e.detail?.message) setError(e.detail.message);
        };

        window.addEventListener('PAYOS_PAYMENT_SUCCESS', handleSuccess);
        window.addEventListener('PAYOS_PAYMENT_ERROR', handleError);
        return () => {
            window.removeEventListener('PAYOS_PAYMENT_SUCCESS', handleSuccess);
            window.removeEventListener('PAYOS_PAYMENT_ERROR', handleError);
            if (autoCloseTimer) clearTimeout(autoCloseTimer);
        };
    }, [orderData]);

    const effectiveCoupon = orderData?.coupon || couponPreview?.coupon || user.paymentDetails?.coupon || null;
    const bonusCredits = orderData?.bonusCredits ?? couponPreview?.bonusCredits ?? user.paymentDetails?.bonusCredits ?? 0;
    const baseCredits = orderData?.baseCredits ?? couponPreview?.baseCredits ?? user.paymentDetails?.baseCredits ?? pkg.credits;
    const totalCredits = orderData?.totalCredits ?? couponPreview?.totalCredits ?? user.paymentDetails?.totalCredits ?? (baseCredits + bonusCredits);
    const payableAmount = orderData?.amountVnd ?? couponPreview?.amountVnd ?? user.paymentDetails?.amountVnd ?? pkg.priceVND;
    const transactionCode = (orderData?.transactionCode || user.paymentDetails?.transactionCode || '...').replace(/-/g, '');
    const amount = payableAmount.toLocaleString('vi-VN');
    const checkoutUrl = orderData?.checkoutUrl;

    const getPopupFeatures = () => {
        const w = 600;
        const h = 700;
        const left = (window.screen.width - w) / 2;
        const top = (window.screen.height - h) / 2;
        return `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=yes`;
    };

    const openPayOSWindow = (url: string) => {
        payosDebugLog('modal_open_popup_attempt', { url });
        const popup = window.open(url, PAYOS_POPUP_WINDOW_NAME, getPopupFeatures());
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
            payosDebugWarn('modal_open_popup_blocked', { url });
            return null;
        }
        payosPopupRef.current = popup;
        payosDebugLog('modal_open_popup_success', { url });
        return popup;
    };

    const ensureOrderForManualTab = async () => {
        if (orderData) return orderData;
        setIsCreatingOrder(true);
        try {
            const data = await onCreateOrder(pkg, activeTab, couponPreview?.code || couponCode.trim() || undefined);
            if (data) {
                setOrderData(data);
                onOrderCreated?.({
                    packageId: pkg.id,
                    transactionCode: data.transactionCode,
                    orderId: data.orderId,
                    coupon: data.coupon ?? couponPreview?.coupon ?? null,
                    baseCredits: data.baseCredits ?? couponPreview?.baseCredits,
                    bonusCredits: data.bonusCredits ?? couponPreview?.bonusCredits,
                    totalCredits: data.totalCredits ?? couponPreview?.totalCredits,
                    amountVnd: data.amountVnd ?? couponPreview?.amountVnd,
                });
            }
            return data;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Không thể tạo đơn thanh toán.');
            return null;
        } finally {
            setIsCreatingOrder(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        if ((activeTab === 'bank' || activeTab === 'momo') && !orderData && !isCreatingOrder) {
            ensureOrderForManualTab();
        }
    }, [isOpen, activeTab]);

    const handleConfirmPayment = async () => {
        if (!orderData) return;
        const hasExistingProof = !!initialProofImageUrl || !!proofPreview;
        
        if (!proofFile && !hasExistingProof) {
            setError('Vui lòng tải lên ảnh bằng chứng chuyển khoản.');
            return;
        }

        if (isProofNoteRequired && !proofNote.trim()) {
            setError('Vui lòng nhập ghi chú bằng chứng giao dịch.');
            return;
        }

        setIsConfirming(true);
        setError(null);

        try {
            if (APP_MODE === 'PRODUCTION') {
                const response = await confirmManualPayment(orderData.orderId, proofFile, proofNote);
                onManualProofSubmitted?.({
                    orderId: orderData.orderId,
                    proofImage: response.order?.proof_image ?? null,
                    proofNote: response.order?.proof_note ?? proofNote,
                });
            } else {
                await new Promise(resolve => setTimeout(resolve, 1500));
                onManualProofSubmitted?.({
                    orderId: orderData.orderId,
                    proofImage: proofPreview ?? initialProofImageUrl ?? null,
                    proofNote,
                });
            }
            setConfirmationStatus('pending');
        } catch (err) {
            setError((err as Error).message || 'Có lỗi xảy ra khi gửi bằng chứng.');
        } finally {
            setIsConfirming(false);
        }
    };

    const handleApplyCoupon = async () => {
        const code = couponCode.trim();
        setCouponError(null);
        if (!code) {
            setCouponPreview(null);
            return;
        }
        if (orderData) {
            setCouponError('Mã KOL cần được áp dụng trước khi tạo đơn thanh toán.');
            return;
        }
        setIsApplyingCoupon(true);
        try {
            if (APP_MODE === 'PRODUCTION') {
                const preview = await previewCreditCoupon(pkg.id, code);
                setCouponPreview(preview);
                setCouponCode(preview.code || code);
            } else {
                setCouponPreview({
                    code,
                    amountVnd: pkg.priceVND,
                    baseCredits: pkg.credits,
                    bonusCredits: Math.max(1, Math.floor(pkg.credits * 0.2)),
                    totalCredits: pkg.credits + Math.max(1, Math.floor(pkg.credits * 0.2)),
                    coupon: {
                        id: 0,
                        code,
                        bonusType: 'percent_credits',
                        bonusValue: 20,
                        referrerId: 0,
                    },
                });
            }
        } catch (err) {
            setCouponPreview(null);
            setCouponError((err as Error).message || 'Không áp dụng được mã KOL.');
        } finally {
            setIsApplyingCoupon(false);
        }
    };

    const requestClose = () => {
        if (confirmationStatus === 'idle') {
            setShowCloseConfirm(true);
        } else {
            resetState();
        }
    };

    const resetState = () => {
        // Nếu đang ở trạng thái success mà chưa gọi onConfirm thì gọi ngay để sync data
        if (confirmationStatus === 'success' && !confirmCalled.current) {
            confirmCalled.current = true;
            onConfirm();
        }
        
        setIsConfirming(false);
        setConfirmationStatus('idle');
        setCopied(false);
        setShowCloseConfirm(false);
        setOrderData(null);
        setProofFile(null);
        setProofPreview(initialProofImageUrl ?? null);
        setProofNote(initialProofNote ?? '');
        setCouponCode('');
        setCouponPreview(null);
        setCouponError(null);
        onClose();
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!isOpen) return null;

    const getBankQR = () => {
        const baseUrl = PAYMENT_CONFIG.methods.bankTransfer?.qrCodeUrl || '';
        if (!baseUrl) return '';
        if (baseUrl.includes('vietqr.io')) {
            try {
                const url = new URL(baseUrl);
                url.searchParams.set('amount', payableAmount.toString());
                url.searchParams.set('addInfo', transactionCode);
                return url.toString();
            } catch (e) {
                return baseUrl;
            }
        }
        return baseUrl;
    };

    const getMomoQR = () => {
        if (!PAYMENT_CONFIG.methods.momo) return '';
        const { phone, name, qrCodeUrl } = PAYMENT_CONFIG.methods.momo;
        const cleanName = name ? name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase() : '';
        if (qrCodeUrl) {
            if (qrCodeUrl.includes('vietqr.io')) {
                try {
                    const url = new URL(qrCodeUrl);
                    url.searchParams.set('amount', payableAmount.toString());
                    url.searchParams.set('addInfo', transactionCode);
                    return url.toString();
                } catch (e) {}
            }
            return qrCodeUrl;
        }
        const momoData = `2|99|${phone}|${cleanName}||0|0|${payableAmount}|${transactionCode}|transfer_my_qr`;
        return `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(momoData)}`;
    };

    const renderCloseConfirm = () => (
        <div className="absolute inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200 p-6">
            <div className="bg-white rounded-[32px] p-8 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-300 text-center">
                <div className="w-16 h-16 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <ExclamationTriangleIcon className="w-8 h-8" />
                </div>
                <h4 className="text-xl font-black text-slate-800 mb-2">Hủy thanh toán?</h4>
                <p className="text-sm text-slate-500 font-medium mb-8 leading-relaxed">Bạn có chắc chắn muốn đóng cửa sổ này?{orderData ? ' Đơn hàng vẫn sẽ được lưu trong lịch sử để bạn thanh toán sau.' : ''}</p>
                <div className="flex flex-col gap-3">
                    <button onClick={resetState} className="w-full py-3.5 bg-rose-500 text-white font-black rounded-2xl shadow-lg shadow-rose-500/20 uppercase text-xs tracking-widest hover:bg-rose-600 transition-all">Đóng cửa sổ</button>
                    <button onClick={() => setShowCloseConfirm(false)} className="w-full py-3.5 bg-slate-100 text-slate-600 font-black rounded-2xl uppercase text-xs tracking-widest hover:bg-slate-200 transition-all">Tiếp tục thanh toán</button>
                </div>
            </div>
        </div>
    );

    const renderSuccess = () => (
        <div className="flex flex-col items-center justify-center p-12 text-center min-h-[450px] animate-in zoom-in-95 duration-500">
            <div className="w-24 h-24 bg-emerald-500 text-white rounded-[40px] flex items-center justify-center shadow-2xl shadow-emerald-500/40 mb-8 animate-bounce">
                <CheckIcon className="w-12 h-12 stroke-[3]" />
            </div>
            <h4 className="text-3xl font-black text-slate-800 tracking-tight mb-4">THANH TOÁN THÀNH CÔNG!</h4>
            <p className="text-slate-500 font-medium max-w-sm mb-10 leading-relaxed text-lg">Hệ thống đã nhận được thanh toán. <strong>{totalCredits.toLocaleString()} {CREDIT}</strong> đã được cộng vào tài khoản của bạn.</p>
            <button onClick={resetState} className="w-full max-w-xs py-4 bg-slate-900 text-white font-black rounded-[20px] shadow-xl shadow-slate-900/20 uppercase text-sm tracking-[0.2em] hover:bg-black hover:-translate-y-1 transition-all active:scale-95">Bắt đầu trải nghiệm</button>
        </div>
    );

    const renderVerifying = () => (
        <div className="flex flex-col items-center justify-center p-12 text-center min-h-[450px] animate-in fade-in duration-300">
            <div className="relative mb-8">
                <div className="w-20 h-20 border-4 border-primary-500/20 border-t-primary-500 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center text-primary-500">
                    <BoltIcon className="w-8 h-8 animate-pulse" />
                </div>
            </div>
            <h4 className="text-xl font-black text-slate-800 uppercase tracking-[0.15em] mb-3">Đang xác thực giao dịch</h4>
            <p className="text-slate-500 font-medium max-w-xs leading-relaxed">Hệ thống đang kết nối trực tiếp với cổng thanh toán để xác nhận số dư của bạn...</p>
        </div>
    );

    const renderPending = () => (
        <div className="flex flex-col items-center justify-center p-12 text-center min-h-[450px] animate-in fade-in duration-300">
            <div className="w-20 h-20 bg-amber-100 text-amber-600 rounded-[32px] flex items-center justify-center shadow-xl shadow-amber-500/10 mb-8">
                <ClockIcon className="w-10 h-10 stroke-[2.5]" />
            </div>
            <h4 className="text-2xl font-black text-slate-800 tracking-tight mb-4">Đang chờ xử lý</h4>
            <p className="text-slate-500 font-medium max-w-sm mb-8 leading-relaxed">Giao dịch của bạn đã được gửi minh chứng thành công. Quản trị viên sẽ kiểm tra và kích hoạt đơn hàng trong ít phút.</p>
            <div className="w-full bg-slate-50 rounded-3xl p-6 border border-slate-100 text-left mb-8">
                <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <InformationCircleIcon className="w-4 h-4" /> Lưu ý quan trọng
                </h5>
                <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-xs font-bold text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 shrink-0"></div>
                        Vui lòng giữ lại biên lai chuyển khoản cho đến khi nhận được thông báo thành công.
                    </li>
                    <li className="flex items-start gap-3 text-xs font-bold text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 shrink-0"></div>
                        Nếu quá 30 phút chưa thấy {CREDIT}, vui lòng liên hệ hỗ trợ {CONTACT_CONFIG.HOTLINE ? <span>hotline: <strong>{CONTACT_CONFIG.HOTLINE}</strong></span> : ''} kèm mã <strong>{transactionCode}</strong>.
                    </li>
                </ul>
            </div>
            <button onClick={resetState} className="w-full max-w-xs py-4 bg-slate-900 text-white font-black rounded-[20px] uppercase text-xs tracking-widest hover:bg-black transition-all">Quay lại trang chính</button>
        </div>
    );

    const handlePayOSClick = async () => {
        payosDebugLog('modal_payos_click_start', {
            packageId: pkg.id,
            packageName: pkg.name,
            priceVND: payableAmount,
        });
        // Thông báo cho người dùng trước khi chuyển sang PayOS
        if (!window.confirm("Hệ thống sẽ mở sang trang thanh toán của PayOS. Vui lòng hoàn tất giao dịch tại đó.")) {
            payosDebugWarn('modal_payos_click_cancelled_by_user');
            return;
        }

        // Mở cửa sổ popup NGAY LẬP TỨC để tránh bị trình duyệt chặn (phải gọi trực tiếp trong click handler)
        const popup = openPayOSWindow('about:blank');
        
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
            payosDebugWarn('modal_payos_popup_not_available');
            alert("Trình duyệt đã chặn cửa sổ bật lên. Vui lòng cho phép hiển thị cửa sổ bật lên (popup) cho trang web này để tiếp tục thanh toán.");
            return;
        }

        setPayosFlowLocked(true);
        payosDebugLog('modal_payos_flow_locked');

        // Ghi nội dung loading vào popup trong khi chờ API
        popup.document.write(`
            <html>
                <head>
                    <title>Đang chuyển hướng...</title>
                    <style>
                        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; background: #f8fafc; color: #1e293b; text-align: center; }
                        .loader { width: 48px; height: 48px; border: 5px solid #e2e8f0; border-bottom-color: #10b981; border-radius: 50%; display: inline-block; box-sizing: border-box; animation: rotation 1s linear infinite; }
                        @keyframes rotation { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } } 
                        h3 { margin-top: 24px; font-weight: 800; letter-spacing: -0.025em; }
                        p { color: #64748b; font-size: 14px; margin-top: 8px; }
                    </style>
                </head>
                <body>
                    <span class="loader"></span>
                    <h3>ĐANG KHỞI TẠO GIAO DỊCH</h3>
                    <p>Vui lòng đợi trong giây lát, hệ thống đang kết nối an toàn với PayOS...</p>
                </body>
            </html>
        `);

        setIsCreatingOrder(true);
        try {
            payosDebugLog('modal_create_order_request', {
                packageId: pkg.id,
                provider: 'payos',
            });
            const data = await onCreateOrder(pkg, 'payos', couponPreview?.code || couponCode.trim() || undefined) as any;
            payosDebugLog('modal_create_order_response', {
                hasData: !!data,
                orderId: data?.orderId ?? null,
                hasCheckoutUrl: !!data?.checkoutUrl,
                transactionCode: data?.transactionCode ?? null,
            });
            if (data?.checkoutUrl) {
                setOrderData(data);
                onOrderCreated?.({
                    packageId: pkg.id,
                    transactionCode: data.transactionCode,
                    orderId: data.orderId,
                    coupon: data.coupon ?? couponPreview?.coupon ?? null,
                    baseCredits: data.baseCredits ?? couponPreview?.baseCredits,
                    bonusCredits: data.bonusCredits ?? couponPreview?.bonusCredits,
                    totalCredits: data.totalCredits ?? couponPreview?.totalCredits,
                    amountVnd: data.amountVnd ?? couponPreview?.amountVnd,
                });
                // Cập nhật URL của popup đã mở
                payosDebugLog('modal_navigate_popup_to_payos', {
                    orderId: data.orderId,
                    checkoutUrl: data.checkoutUrl,
                });
                popup.location.href = data.checkoutUrl;
            } else {
                popup.close();
                payosPopupRef.current = null;
                setPayosFlowLocked(false);
                payosDebugWarn('modal_missing_checkout_url', {
                    orderId: data?.orderId ?? null,
                    paymentError: data?.paymentError ?? null,
                });
                setError(data?.paymentError || 'Không thể tạo đơn hàng thanh toán. Vui lòng thử lại.');
            }
        } catch (e) {
            console.error("Lỗi khi tạo đơn hàng PayOS:", e);
            popup.close();
            payosPopupRef.current = null;
            setPayosFlowLocked(false);
            payosDebugError('modal_create_order_failed', {
                error: e instanceof Error ? e.message : String(e),
            });
            setError(e instanceof Error ? e.message : 'Có lỗi xảy ra khi kết nối với cổng thanh toán.');
        } finally {
            setIsCreatingOrder(false);
        }
    };

    const renderMainContent = () => (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="px-8 py-4 pr-16 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-slate-200 shadow-sm">
                        <SparklesIcon className="w-5 h-5 text-primary-500" />
                    </div>
                    <div>
                        <h4 className="text-base font-black text-slate-800 leading-tight mb-0.5">{pkg.name}</h4>
                        <div className="flex items-center gap-2">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Mã GD:</span>
                            <code className="text-[9px] font-black text-primary-600 bg-primary-50 px-2 py-0.5 rounded">{transactionCode}</code>
                        </div>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-xl font-black text-primary-600 leading-none">{amount} đ</div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">Số tiền thanh toán</p>
                </div>
            </div>

            <div className="px-8 pt-4 shrink-0">
                <div className="rounded-3xl border border-amber-100 bg-amber-50/40 p-4">
                    <div className="flex flex-col md:flex-row gap-3 md:items-end">
                        <div className="flex-1 space-y-1.5">
                            <label className="text-[9px] font-black uppercase tracking-widest text-amber-600">Mã KOL tặng thêm credits</label>
                            <div className="flex gap-2">
                                <input
                                    value={couponCode}
                                    onChange={(e) => {
                                        setCouponCode(e.target.value.toUpperCase());
                                        setCouponPreview(null);
                                        setCouponError(null);
                                    }}
                                    disabled={!!orderData || isCreatingOrder || payosFlowLocked}
                                    placeholder="Nhập mã KOL nếu có"
                                    className="min-w-0 flex-1 rounded-2xl border border-amber-100 bg-white px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-700 outline-none transition-all focus:border-amber-400 focus:ring-4 focus:ring-amber-500/10 disabled:bg-slate-100 disabled:text-slate-400"
                                />
                                <button
                                    type="button"
                                    onClick={handleApplyCoupon}
                                    disabled={isApplyingCoupon || !!orderData || isCreatingOrder || payosFlowLocked}
                                    className="rounded-2xl bg-amber-500 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-amber-500/20 transition-all hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {isApplyingCoupon ? 'Đang kiểm tra...' : 'Áp dụng'}
                                </button>
                            </div>
                            {couponError && <p className="text-[10px] font-bold text-rose-500">{couponError}</p>}
                            {effectiveCoupon && bonusCredits > 0 && (
                                <p className="text-[10px] font-bold text-emerald-600">
                                    Đã áp dụng mã {effectiveCoupon.code}: tặng thêm {bonusCredits.toLocaleString('vi-VN')} {CREDIT.toLowerCase()}. Giá thanh toán giữ nguyên.
                                </p>
                            )}
                        </div>
                        <div className="rounded-2xl bg-white px-4 py-3 text-right border border-amber-100 min-w-[180px]">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Credits nhận</p>
                            <p className="text-lg font-black text-slate-800">
                                {baseCredits.toLocaleString('vi-VN')}
                                {bonusCredits > 0 && <span className="text-emerald-600"> + {bonusCredits.toLocaleString('vi-VN')}</span>}
                            </p>
                            <p className="text-[10px] font-bold text-slate-400">Tổng: {totalCredits.toLocaleString('vi-VN')} {CREDIT.toLowerCase()}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-8 pt-4 flex items-center gap-2 shrink-0 flex-wrap">
                {showPayOSMethod && (
                    <button 
                        onClick={() => setActiveTab('payos')}
                        disabled={payosFlowLocked || isCreatingOrder}
                        className={`flex-1 min-w-[110px] py-2.5 px-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all border-2 ${activeTab === 'payos' ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'bg-white border-slate-100 text-slate-400 hover:border-emerald-200 hover:text-emerald-500'} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        <BoltIcon className="w-3.5 h-3.5" /> Tự động
                    </button>
                )}
                {showBankMethod && (
                    <button 
                        onClick={() => setActiveTab('bank')}
                        disabled={payosFlowLocked || isCreatingOrder}
                        className={`flex-1 min-w-[110px] py-2.5 px-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all border-2 ${activeTab === 'bank' ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-600/20' : 'bg-white border-slate-100 text-slate-400 hover:border-blue-200 hover:text-blue-600'} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        <CreditCardIcon className="w-3.5 h-3.5" /> Ngân hàng
                    </button>
                )}
                {showMomoMethod && (
                    <button 
                        onClick={() => setActiveTab('momo')}
                        disabled={payosFlowLocked || isCreatingOrder}
                        className={`flex-1 min-w-[110px] py-2.5 px-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all border-2 ${activeTab === 'momo' ? 'bg-pink-500 border-pink-500 text-white shadow-lg shadow-pink-500/20' : 'bg-white border-slate-100 text-slate-400 hover:border-pink-200 hover:text-pink-500'} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        <WalletIcon className="w-3.5 h-3.5" /> Ví MoMo
                    </button>
                )}
            </div>

            {error && activeTab === 'payos' && (
                <div className="mx-8 mt-4 p-3 rounded-xl bg-rose-50 border border-rose-100 flex items-center gap-2 text-rose-600 animate-shake">
                    <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
                    <span className="text-[10px] font-bold">{error}</span>
                </div>
            )}

            <div className="flex-grow p-8 overflow-y-auto custom-scrollbar">
                {activeTab === 'payos' && (
                    <div className="flex flex-col items-center justify-center h-full min-h-[300px] animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-3xl flex items-center justify-center mb-5">
                            <BoltIcon className="w-8 h-8 animate-pulse" />
                        </div>
                        <h5 className="text-lg font-black text-slate-800 mb-2 uppercase tracking-tight">Kích hoạt tức thì</h5>
                        <p className="text-xs text-slate-500 font-medium text-center max-w-xs mb-4 leading-relaxed">Hệ thống tự động nhận diện và cộng {CREDIT} ngay sau khi bạn thực hiện chuyển khoản thành công qua PayOS.</p>
                        <button 
                            type="button"
                            onClick={handlePayOSClick}
                            disabled={isCreatingOrder || payosFlowLocked}
                            className="w-full max-w-sm py-4 bg-emerald-500 text-white font-black rounded-2xl shadow-xl shadow-emerald-500/30 hover:bg-emerald-600 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3 text-xs uppercase tracking-[0.15em] disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {isCreatingOrder ? (
                                <><Spinner size="sm" /> <span>Đang chuẩn bị...</span></>
                            ) : payosFlowLocked ? (
                                <><ClockIcon className="w-5 h-5" /> <span>Đang chờ thanh toán...</span></>
                            ) : (
                                <>Thanh toán qua PayOS <i className="ti ti-external-link text-lg"></i></>
                            )}
                        </button>
                        <p className="mt-6 text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                            <i className="ti ti-shield-check text-sm text-emerald-500"></i> An toàn & Bảo mật 256-bit
                        </p>
                    </div>
                )}

                {(activeTab === 'bank' || activeTab === 'momo') && (
                    <div className="flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        {isCreatingOrder ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Spinner size="lg" />
                                <p className="mt-4 text-xs text-slate-500">Đang tạo đơn hàng...</p>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                <div className="grid md:grid-cols-2 gap-8 items-start">
                                    <div className="flex flex-col items-center justify-center">
                                        <div className="relative group w-full aspect-square max-w-[200px]">
                                            <div className="absolute inset-0 bg-primary-500/10 rounded-3xl blur-xl group-hover:bg-primary-500/20 transition-all"></div>
                                            <div className="relative bg-white rounded-3xl p-3 border border-slate-100 shadow-lg group-hover:border-primary-200 transition-all">
                                                <img src={activeTab === 'bank' ? getBankQR() : getMomoQR()} alt="Payment QR" className="w-full h-full object-contain rounded-2xl" />
                                                <div className="absolute inset-0 bg-white/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center rounded-3xl backdrop-blur-[1px]">
                                                    <button onClick={() => copyToClipboard(transactionCode)} className="px-4 py-2 bg-slate-900 text-white text-[9px] font-black rounded-xl uppercase tracking-widest shadow-lg hover:bg-black transition-all">
                                                        {copied ? 'Đã copy mã' : 'Copy nội dung'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <p className="mt-4 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                            {activeTab === 'momo' ? 'Mở MoMo để quét mã' : 'Quét mã để thanh toán'}
                                        </p>
                                    </div>
                                    <div className="flex flex-col justify-center space-y-4">
                                        <div className="space-y-1">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Nội dung chuyển khoản</p>
                                            <div className="flex items-center justify-between bg-rose-50 border border-rose-100 p-3 rounded-xl group relative overflow-hidden">
                                                <div className="absolute top-0 left-0 w-1 h-full bg-rose-500"></div>
                                                <code className="text-lg font-black text-rose-600 tracking-[0.1em] uppercase">{transactionCode}</code>
                                                <button onClick={() => copyToClipboard(transactionCode)} className="p-1.5 hover:bg-white rounded-lg text-rose-400 hover:text-rose-600 transition-all">
                                                    <CopyIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <p className="text-[8px] text-rose-400 font-bold italic">* Phải nhập chính xác nội dung này</p>
                                        </div>
                                        <div className="space-y-2 pt-3 border-t border-slate-100">
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-slate-400 font-medium">Ngân hàng:</span>
                                                <span className="font-black text-slate-700">{activeTab === 'bank' ? PAYMENT_CONFIG.methods.bankTransfer.bankName : 'MoMo Wallet'}</span>
                                            </div>
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-slate-400 font-medium">Số tài khoản:</span>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="font-black text-slate-700">{activeTab === 'bank' ? PAYMENT_CONFIG.methods.bankTransfer.accountNumber : PAYMENT_CONFIG.methods.momo.phone}</span>
                                                    <button onClick={() => copyToClipboard(activeTab === 'bank' ? PAYMENT_CONFIG.methods.bankTransfer.accountNumber : PAYMENT_CONFIG.methods.momo.phone)} className="text-slate-300 hover:text-primary-500"><CopyIcon className="w-3 h-3" /></button>
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-slate-400 font-medium">Chủ tài khoản:</span>
                                                <span className="font-black text-slate-700 uppercase">{activeTab === 'bank' ? PAYMENT_CONFIG.methods.bankTransfer.accountName : PAYMENT_CONFIG.methods.momo.name}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6 items-start">
                                    <div 
                                        className={`relative group rounded-2xl border-2 border-dashed transition-all cursor-pointer ${dragActive ? 'border-primary-500 bg-primary-50/50' : proofFile ? 'border-emerald-500 bg-emerald-50/5' : 'border-slate-200 hover:border-primary-400 bg-slate-50/50'}`}
                                        onDragEnter={onDrag}
                                        onDragLeave={onDrag}
                                        onDragOver={onDrag}
                                        onDrop={onDrop}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <input 
                                            ref={fileInputRef}
                                            type="file" 
                                            accept="image/*" 
                                            className="hidden" 
                                            onChange={onFileChange} 
                                        />
                                        <div className="flex flex-col items-center justify-center p-4 text-center min-h-[140px]">
                                            {proofPreview ? (
                                                <div className="relative w-full aspect-video rounded-lg overflow-hidden group/preview shadow-md">
                                                    <img src={proofPreview} className="w-full h-full object-cover" alt="Proof preview" />
                                                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                                                        <span className="text-white text-[9px] font-black uppercase tracking-widest">Thay đổi ảnh</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <CloudArrowUpIcon className="w-8 h-8 text-slate-300 mb-2 group-hover:text-primary-500 transition-colors" />
                                                    <p className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Gửi minh chứng</p>
                                                    <p className="text-[8px] text-slate-400 font-bold mt-1">Kéo thả hoặc nhấn để chọn ảnh</p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center justify-between">
                                            Ghi chú (Nếu có)
                                            {isProofNoteRequired && <span className="text-rose-500">Bắt buộc</span>}
                                        </label>
                                        <textarea 
                                            value={proofNote}
                                            onChange={(e) => setProofNote(e.target.value)}
                                            placeholder="VD: MBBank lúc 14:30..."
                                            className={`w-full p-3 rounded-2xl bg-slate-50 border focus:bg-white transition-all outline-none text-xs font-medium resize-none h-[140px] ${error && !proofNote && isProofNoteRequired ? 'border-rose-300' : 'border-slate-100 focus:border-primary-500 shadow-inner'}`}
                                        ></textarea>
                                    </div>
                                </div>

                                {error && (
                                    <div className="p-3 rounded-xl bg-rose-50 border border-rose-100 flex items-center gap-2 text-rose-600 animate-shake">
                                        <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
                                        <span className="text-[10px] font-bold">{error}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="px-8 py-5 bg-white border-t border-slate-50 flex flex-col gap-3 shrink-0">
                {activeTab !== 'payos' && (
                    <button 
                        onClick={handleConfirmPayment} 
                        disabled={isConfirming || isCreatingOrder} 
                        className={`w-full py-3.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-[0.15em] rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:bg-slate-400 ${!proofFile ? 'opacity-50' : 'hover:bg-black hover:-translate-y-0.5 active:scale-[0.98]'}`}
                    >
                        {isConfirming ? <Spinner size="sm" /> : <><CloudArrowUpIcon className="w-4 h-4" /> {initialProofImageUrl ? 'Gửi lại minh chứng thanh toán' : 'Gửi minh chứng thanh toán'}</>}
                    </button>
                )}
                <div className="flex items-center justify-center gap-4">
                    <button onClick={requestClose} className="text-[9px] font-black text-slate-400 hover:text-rose-500 uppercase tracking-widest transition-colors flex items-center gap-1.5">
                        <XMarkIcon className="w-3 h-3" /> Hủy giao dịch
                    </button>
                    <div className="h-3 w-px bg-slate-200"></div>
                    <p className="text-[9px] font-bold text-slate-400 flex items-center gap-1.5">
                        <i className="ti ti-headset text-sm text-primary-500"></i>
                        {CONTACT_CONFIG.HOTLINE ? (
                            <a href={`tel:${String(CONTACT_CONFIG.HOTLINE).replace(/\s/g, '')}`} className="hover:text-primary-600">Hỗ trợ: {CONTACT_CONFIG.HOTLINE}</a>
                        ) : (
                            'Liên hệ hỗ trợ'
                        )}
                    </p>
                </div>
            </div>
        </div>
    );

    const modalTree = (
        <div 
            className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300 pointer-events-auto" 
            onClick={payosFlowLocked ? (e) => e.stopPropagation() : requestClose}
        >
            {payosFlowLocked && (
                <div
                    className="absolute inset-0 z-[100010] flex flex-col items-center justify-center bg-slate-900/95 backdrop-blur-xl animate-in fade-in duration-200 px-6"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="w-20 h-20 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin mb-8"></div>
                    <h4 className="text-white text-xl font-black uppercase tracking-widest mb-2 text-center">Đang xử lý tại PayOS</h4>
                    <p className="text-slate-300 font-medium text-center max-w-xs mb-10 leading-relaxed">Vui lòng hoàn tất thanh toán trong cửa sổ mới. Hệ thống sẽ tự động cập nhật ngay khi bạn thanh toán xong.</p>

                    <div className="flex flex-col gap-3 w-full max-w-xs">
                        <div className="p-4 bg-white/5 border border-white/10 rounded-2xl flex items-center gap-3">
                            <InformationCircleIcon className="w-5 h-5 text-emerald-400 shrink-0" />
                            <span className="text-xs text-slate-300 font-medium italic">Không thấy cửa sổ thanh toán? Vui lòng kiểm tra tab trình duyệt.</span>
                        </div>

                        {!!checkoutUrl && (
                            <button
                                type="button"
                                onClick={() => {
                                    payosDebugLog('modal_reopen_popup_click', { checkoutUrl });
                                    const reopened = openPayOSWindow(checkoutUrl);
                                    if (!reopened) {
                                        payosDebugWarn('modal_reopen_popup_failed');
                                        setError('Không thể mở lại cửa sổ PayOS. Vui lòng kiểm tra cài đặt popup của trình duyệt.');
                                    }
                                }}
                                className="w-full py-4 bg-emerald-500/90 hover:bg-emerald-500 text-white font-black rounded-2xl text-[10px] uppercase tracking-widest transition-all"
                            >
                                Mở lại cửa sổ thanh toán
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={() => {
                                payosDebugWarn('modal_manual_cancel_click');
                                try {
                                    if (payosPopupRef.current && !payosPopupRef.current.closed) {
                                        payosPopupRef.current.close();
                                    }
                                } catch {
                                    /* cross-origin */
                                }
                                setPayosFlowLocked(false);
                                payosPopupRef.current = null;
                            }}
                            className="w-full py-4 bg-white/10 hover:bg-white/20 text-white font-black rounded-2xl text-[10px] uppercase tracking-widest transition-all border border-white/20"
                        >
                            Hủy và quay lại
                        </button>
                    </div>
                </div>
            )}
            <div className="bg-white rounded-[48px] shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh] relative z-[10010] animate-in slide-in-from-bottom-8 duration-500" onClick={e => e.stopPropagation()}>
                <button 
                    onClick={requestClose} 
                    disabled={payosFlowLocked}
                    className="absolute top-6 right-6 z-20 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-lg flex items-center justify-center text-slate-400 hover:text-slate-800 transition-all border border-slate-100 disabled:opacity-0 disabled:pointer-events-none"
                >
                    <XMarkIcon className="w-6 h-6" />
                </button>

                {confirmationStatus === 'idle' && renderMainContent()}
                {confirmationStatus === 'verifying' && renderVerifying()}
                {confirmationStatus === 'success' && renderSuccess()}
                {confirmationStatus === 'pending' && renderPending()}
                
                {showCloseConfirm && renderCloseConfirm()}
            </div>
        </div>
    );

    if (typeof document !== 'undefined' && document.body) {
        return createPortal(modalTree, document.body);
    }
    return modalTree;
};

export default PaymentModal;
